/**
 * AI CONTEXT
 * Purpose: core Firebase<->Jotai subscription bridge hooks (one/some/all/filtered docs).
 * Handles: atom updates, stable ID/query keys, subscription pooling, loop/throttle guards.
 * Exposes: useOneDoc/useSomeDocs/useAllDocs/useFilteredDocs/useStableIds.
 * Used by: nearly all data hooks and context providers.
 * Refactor: split perf/loop-protection code from hook logic for maintainability.
 */
import { useAtomValue, useSetAtom } from "jotai"
import { uniq } from "lodash"
import { useEffect, useMemo, useRef } from "react"

import { DocAtoms } from "#atoms/createDocAtoms"
import { getSubscriptionPool } from "#atoms/subscriptionPool"
import Firebase from "#classes/Firebase"
import { useAuthorizationContext } from "#contexts/AuthorizationContextProvider"
import {
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DataWithId } from "#customTypes/types"
import { stableStringify } from "#utils/textFormatters"

/**
 * INFINITE LOOP PROTECTION
 * Tracks subscription attempts to detect and prevent infinite loops
 */
const ENABLE_PERF_LOGGING = false
const ENABLE_THROTTLING = false // Set to false to disable throttling for performance comparison
const THROTTLE_MS = 50 // Minimum time between re-subscriptions (lenient for dev mode)
const MAX_ATTEMPTS_PER_MINUTE = 100 // Max subscription attempts per minute (lenient for dev mode)
const INITIAL_MOUNT_GRACE_PERIOD = 2000 // First 2000ms after page load is exempt from throttling (increased for pooling + StrictMode)
const subscriptionAttempts = new Map<string, number[]>()
const warningLogged = new Map<string, number>() // Track when we last warned for each key
const pageLoadTime = Date.now()

/**
 * PERFORMANCE TRACKING
 * Tracks total time spent in subscription operations
 */
interface PerformanceMetrics {
  totalSubscriptionTime: number
  totalLoopCheckTime: number
  totalPoolTime: number
  totalFirebaseTime: number
  subscriptionCount: number
  blockedCount: number
  startTime: number
  lastLogTime: number
}

const perfMetrics: PerformanceMetrics = {
  totalSubscriptionTime: 0,
  totalLoopCheckTime: 0,
  totalPoolTime: 0,
  totalFirebaseTime: 0,
  subscriptionCount: 0,
  blockedCount: 0,
  startTime: pageLoadTime,
  lastLogTime: pageLoadTime,
}

let logTimeout: ReturnType<typeof setTimeout> | null = null
const QUIET_PERIOD_MS = 5000 // Log after 5 seconds of no new subscriptions

function scheduleLog() {
  if (!ENABLE_PERF_LOGGING) return

  // Clear existing timeout
  if (logTimeout) {
    clearTimeout(logTimeout)
  }

  // Schedule new log after quiet period
  logTimeout = setTimeout(() => {
    logPerformanceSummary("(AFTER QUIET PERIOD)")
    logTimeout = null
  }, QUIET_PERIOD_MS)
}

function logPerformanceSummary(label = "SUMMARY") {
  const totalTime = Date.now() - perfMetrics.startTime
  const timeSinceLastLog = Date.now() - perfMetrics.lastLogTime
  const totalOperationTime =
    perfMetrics.totalSubscriptionTime +
    perfMetrics.totalLoopCheckTime +
    perfMetrics.totalPoolTime +
    perfMetrics.totalFirebaseTime

  console.log(
    `\n📊 SUBSCRIPTION PERFORMANCE ${label} (throttling: ${ENABLE_THROTTLING ? "ON" : "OFF"})\n` +
      `  ⏱️  TOTAL OPERATION TIME: ${totalOperationTime.toFixed(2)}ms\n` +
      `     ├─ Hook execution: ${perfMetrics.totalSubscriptionTime.toFixed(2)}ms\n` +
      `     ├─ Loop checks: ${perfMetrics.totalLoopCheckTime.toFixed(2)}ms\n` +
      `     ├─ Pool operations: ${perfMetrics.totalPoolTime.toFixed(2)}ms\n` +
      `     └─ Firebase subscriptions: ${perfMetrics.totalFirebaseTime.toFixed(2)}ms\n` +
      `  📈 Stats: ${perfMetrics.subscriptionCount} attempted, ${perfMetrics.blockedCount} blocked\n` +
      `  ⏳ Elapsed: ${totalTime.toFixed(2)}ms total (${timeSinceLastLog.toFixed(2)}ms since last log)\n`
  )
  perfMetrics.lastLogTime = Date.now()
}

// Expose functions to manually log metrics and get current metrics
if (typeof window !== "undefined") {
  const win = window as Window & {
    logSubscriptionMetrics?: () => void
    getSubscriptionMetrics?: () => PerformanceMetrics & { totalElapsed: number }
  }
  win.logSubscriptionMetrics = () => logPerformanceSummary("(MANUAL)")
  win.getSubscriptionMetrics = () => ({
    ...perfMetrics,
    totalElapsed: Date.now() - perfMetrics.startTime,
  })
}

// Export function for pool to report Firebase subscription time
export function reportFirebaseSubscriptionTime(timeMs: number) {
  perfMetrics.totalFirebaseTime += timeMs
}

function checkInfiniteLoop(key: string, logPrefix: string): boolean {
  const loopCheckStart = performance.now()

  if (!ENABLE_THROTTLING) {
    perfMetrics.totalLoopCheckTime += performance.now() - loopCheckStart
    return false // Always allow when throttling is disabled
  }

  const now = Date.now()
  const oneMinuteAgo = now - 60000
  const timeSincePageLoad = now - pageLoadTime

  // Get or initialize attempts for this key
  if (!subscriptionAttempts.has(key)) {
    subscriptionAttempts.set(key, [])
  }

  const attempts = subscriptionAttempts.get(key)!

  // Remove attempts older than 1 minute
  const recentAttempts = attempts.filter((time) => time > oneMinuteAgo)
  subscriptionAttempts.set(key, recentAttempts)

  // During initial grace period (for React StrictMode double-mounting), be more lenient
  const isInGracePeriod = timeSincePageLoad < INITIAL_MOUNT_GRACE_PERIOD

  if (isInGracePeriod) {
    // Still record the attempt but don't block
    recentAttempts.push(now)
    subscriptionAttempts.set(key, recentAttempts)
    perfMetrics.totalLoopCheckTime += performance.now() - loopCheckStart
    return false
  }

  // Check if we're hitting the rate limit
  if (recentAttempts.length >= MAX_ATTEMPTS_PER_MINUTE) {
    console.error(
      `${logPrefix} 🚨 INFINITE LOOP DETECTED! Too many subscription attempts (${recentAttempts.length} in last minute) for key: ${key.slice(0, 100)}`
    )
    console.error(
      `${logPrefix} ⛔ SUBSCRIPTION BLOCKED to prevent infinite loop. Check your dependencies!`
    )
    perfMetrics.blockedCount++
    perfMetrics.totalLoopCheckTime += performance.now() - loopCheckStart
    return true // Block subscription
  }

  // Check if last attempt was too recent (throttling)
  // Allow first 2 attempts without throttle (for StrictMode double-mount)
  if (recentAttempts.length >= 2) {
    const lastAttempt = recentAttempts[recentAttempts.length - 1]
    if (lastAttempt && now - lastAttempt < THROTTLE_MS) {
      console.warn(
        `${logPrefix} ⏱️ Subscription throttled (too soon after last attempt). Key: ${key.slice(0, 100)}`
      )
      perfMetrics.blockedCount++
      perfMetrics.totalLoopCheckTime += performance.now() - loopCheckStart
      return true // Block subscription
    }
  }

  // Log warning if subscription rate is high (but throttle the warning itself)
  if (recentAttempts.length > MAX_ATTEMPTS_PER_MINUTE / 2) {
    const lastWarning = warningLogged.get(key) ?? 0
    const timeSinceLastWarning = now - lastWarning

    // Only warn once per 10 seconds per key to reduce noise
    if (timeSinceLastWarning > 10000) {
      console.warn(
        `${logPrefix} ⚠️ High subscription rate detected (${recentAttempts.length} in last minute, threshold: ${MAX_ATTEMPTS_PER_MINUTE}). Check for unstable dependencies! Key: ${key.slice(0, 100)}`
      )
      warningLogged.set(key, now)
    }
  }

  // Record this attempt
  recentAttempts.push(now)
  subscriptionAttempts.set(key, recentAttempts)

  perfMetrics.totalLoopCheckTime += performance.now() - loopCheckStart
  return false // Allow subscription
}

export const useStableIds = (ids: Uuid[] | undefined) => {
  const idsString = useMemo(() => {
    if (!ids?.length) return ""

    // Warn if duplicates are found
    const deduped = uniq(ids)
    // if (DEBUG_MODE && deduped.length !== ids.length) {
    //   console.warn(
    //     `useStableIds: ${ids.length - deduped.length} duplicate ID(s) removed from ${ids.length} total`
    //   )
    // }
    return deduped.sort().join(",")
  }, [ids])

  const stableIds = useMemo(() => {
    return idsString.length ? idsString.split(",") : []
  }, [idsString])
  return stableIds
}

/**
 * FIREBASE + JOTAI HOOKS
 *
 * These hooks bridge Firebase real-time subscriptions with Jotai atomic state.
 *
 * Flow:
 * 1. Hook subscribes to Firebase listener
 * 2. Firebase sends changes via callback
 * 3. Callback updates Jotai atoms
 * 4. Components re-render only if their specific atom changed
 *
 * Benefits:
 * - Atomic updates (only affected components re-render)
 * - Automatic subscription cleanup
 * - Type-safe
 * - Extensible to all doc types
 */

/**
 * Hook: Subscribe to a single document by ID
 *
 * @param atoms - The doc atoms created by createDocAtoms
 * @param docType - Firebase collection name
 * @param id - Document ID to watch
 *
 * @returns The document data (or null if not found/loading)
 */
export function useOneDoc<T extends DataWithId>(
  atoms: DocAtoms<T>,
  docType: DOC_TYPES,
  id: Uuid | null | undefined
): T | null {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const { logPrefix, updateDocsAtom, docByIdAtom } = atoms
  const updateDocs = useSetAtom(updateDocsAtom)
  const doc = useAtomValue(docByIdAtom(id ?? null))

  useEffect(() => {
    const subscriptionStart = performance.now()
    perfMetrics.subscriptionCount++

    if (!id || !isManagerLoggedIn) {
      // console.log(
      //   `${logPrefix} ⏸️ useOneDoc skipped (id: ${id}, logged in: ${isManagerLoggedIn})`
      // )
      return
    }

    // Infinite loop protection
    const loopKey = `useOneDoc:${docType}:${id}`
    if (checkInfiniteLoop(loopKey, logPrefix)) {
      perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
      scheduleLog()
      return // Subscription blocked
    }

    // console.log(`${logPrefix} 🎯 useOneDoc registering ID with pool: ${id}`)

    // Get pool and register this ID
    const poolStart = performance.now()
    const pool = getSubscriptionPool<T>(docType, updateDocs)
    pool.registerIds([id])
    perfMetrics.totalPoolTime += performance.now() - poolStart

    perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
    scheduleLog()

    return () => {
      // console.log(`${logPrefix} 🎯 useOneDoc unregistering ID from pool: ${id}`)
      const unregisterStart = performance.now()
      pool.unregisterIds([id])
      perfMetrics.totalPoolTime += performance.now() - unregisterStart
    }
  }, [id, isManagerLoggedIn, docType, logPrefix, updateDocs, docByIdAtom])

  return doc
}

/**
 * Hook: Subscribe to multiple documents by IDs
 * Handles Firebase's 30-doc batch limit automatically
 *
 * @param atoms - The doc atoms created by createDocAtoms
 * @param docType - Firebase collection name
 * @param ids - Array of document IDs to watch
 *
 * @returns Array of documents
 */
export function useSomeDocs<T extends DataWithId>(
  atoms: DocAtoms<T>,
  docType: DOC_TYPES,
  ids: Uuid[] | undefined
): T[] {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const { logPrefix, docIdsForBatchAtom, updateDocsAtom, docsBatchAtom } = atoms

  // Memoize and stabilize the IDs array - this becomes our atom key
  // IMPORTANT: We stringify the sorted IDs to create a stable dependency
  const stableIds = useStableIds(ids)
  const idsString = useMemo(() => stableIds.join(","), [stableIds])

  // Write to ID list and get update callback
  const setBatchIds = useSetAtom(docIdsForBatchAtom(idsString))
  const updateDocs = useSetAtom(updateDocsAtom)

  // Read from derived atom (automatically maps IDs → docs)
  const rawDocs = useAtomValue(docsBatchAtom(idsString))

  // Memoize to prevent new array reference when docs haven't actually changed.
  // The derived atom creates a new array on every read, so we stabilize it here.
  // We compare by element reference identity — Jotai returns new object refs for updated atoms.
  const prevDocsRef = useRef<T[]>([])
  const docs = useMemo(() => {
    const prev = prevDocsRef.current
    if (
      prev.length > 0 &&
      rawDocs.length === prev.length &&
      rawDocs.every((doc, i) => doc === prev[i])
    ) {
      return prev
    }

    prevDocsRef.current = rawDocs
    return rawDocs
  }, [rawDocs])

  useEffect(() => {
    const subscriptionStart = performance.now()
    perfMetrics.subscriptionCount++

    if (!stableIds.length || !isManagerLoggedIn) {
      // console.log(
      //   `${logPrefix} ⏸️ useSomeDocs skipped (count: ${stableIds.length}, logged in: ${isManagerLoggedIn})`
      // )
      setBatchIds([])
      return
    }

    // Infinite loop protection
    const loopKey = `useSomeDocs:${docType}:${idsString.slice(0, 100)}`
    if (checkInfiniteLoop(loopKey, logPrefix)) {
      perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
      scheduleLog()
      return // Subscription blocked
    }

    // console.log(
    //   `${logPrefix} 🎯 useSomeDocs registering ${stableIds.length} IDs with pool`
    // )

    // Initialize the batch with the requested IDs
    setBatchIds(stableIds)

    // Register with pool (pool handles the actual Firebase subscription)
    const poolStart = performance.now()
    const pool = getSubscriptionPool<T>(docType, updateDocs)
    pool.registerBatch(stableIds, idsString)
    perfMetrics.totalPoolTime += performance.now() - poolStart

    perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
    scheduleLog()

    return () => {
      // console.log(
      //   `${logPrefix} 🎯 useSomeDocs unregistering ${stableIds.length} IDs from pool`
      // )
      const unregisterStart = performance.now()
      pool.unregisterBatch(idsString)
      perfMetrics.totalPoolTime += performance.now() - unregisterStart
    }
  }, [
    idsString,
    isManagerLoggedIn,
    docType,
    logPrefix,
    updateDocs,
    stableIds,
    setBatchIds,
  ])

  return docs
}

/**
 * Hook: Subscribe to all documents in a collection
 *
 * @param atoms - The doc atoms created by createDocAtoms
 * @param docType - Firebase collection name
 *
 * @returns Array of all documents
 */
export function useAllDocs<T extends DataWithId>(
  atoms: DocAtoms<T>,
  docType: DOC_TYPES
): T[] {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const { logPrefix, allDocIdsAtom, updateDocsAtom, allDocsAtom } = atoms

  // Write to ID list and use batch updater for individual docs
  const setAllDocIds = useSetAtom(allDocIdsAtom)
  const updateDocs = useSetAtom(updateDocsAtom)

  // Read from derived atom (automatically maps IDs → docs)
  const rawAllDocs = useAtomValue(allDocsAtom)

  // Memoize to prevent new array reference when docs haven't actually changed.
  // We compare by element reference identity — Jotai returns new object refs for updated atoms.
  const prevAllDocsRef = useRef<T[]>([])
  const allDocs = useMemo(() => {
    const prev = prevAllDocsRef.current
    if (
      prev.length > 0 &&
      rawAllDocs.length === prev.length &&
      rawAllDocs.every((doc, i) => doc === prev[i])
    ) {
      return prev
    }

    prevAllDocsRef.current = rawAllDocs
    return rawAllDocs
  }, [rawAllDocs])

  useEffect(() => {
    const subscriptionStart = performance.now()
    perfMetrics.subscriptionCount++

    if (!isManagerLoggedIn) {
      // console.log(`${logPrefix} ⏸️ useAllDocs skipped (not logged in)`)
      return
    }

    // Infinite loop protection
    const loopKey = `useAllDocs:${docType}`
    if (checkInfiniteLoop(loopKey, logPrefix)) {
      perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
      scheduleLog()
      return // Subscription blocked
    }

    // console.log(`${logPrefix} 🔌 useAllDocs subscribing to all docs`)

    const firebaseStart = performance.now()
    const unsubscribe = Firebase.getInstance().createListenerForAllDocs<T>(
      docType,
      (changes: Array<{ doc: T; type: string }>) => {
        // console.log(
        //   `${logPrefix} 📥 useAllDocs received ${changes.length} changes`
        // )

        // Update individual doc atoms atomically
        const docUpdates = changes.map(
          ({ doc, type }: { doc: T; type: string }) => ({
            id: doc.id,
            doc: type === "removed" ? null : doc,
          })
        )
        updateDocs(docUpdates)

        // Update ID list
        setAllDocIds((prevIds) => {
          const newIds = new Set(prevIds)

          changes.forEach(({ doc, type }: { doc: T; type: string }) => {
            if (type === "added" || type === "modified") {
              newIds.add(doc.id)
            } else if (type === "removed") {
              newIds.delete(doc.id)
            }
          })

          const finalIds = Array.from(newIds)
          // console.log(
          //   `${logPrefix} ✅ useAllDocs updated, total IDs: ${finalIds.length}`
          // )
          return finalIds
        })
      }
    )
    perfMetrics.totalPoolTime += performance.now() - firebaseStart

    perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
    scheduleLog()

    return () => {
      // console.log(`${logPrefix} 🔌 useAllDocs unsubscribing`)
      unsubscribe()
    }
  }, [isManagerLoggedIn, docType, logPrefix, setAllDocIds, updateDocs])

  return allDocs
}

/**
 * Hook: Subscribe to documents matching a query
 *
 * @param atoms - The doc atoms created by createDocAtoms
 * @param docType - Firebase collection name
 * @param queryConfig - Firebase query configuration (must be memoized!)
 *
 * @returns Array of filtered documents
 */
export function useFilteredDocs<T extends DataWithId>(
  atoms: DocAtoms<T>,
  docType: DOC_TYPES,
  queryConfig: FirebaseQueryConfig
): T[] {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const { logPrefix, filteredDocIdsAtom, updateDocsAtom, filteredDocsAtom } =
    atoms

  // Create stable query key for this specific query
  const queryKey = useMemo(() => {
    const key = stableStringify(queryConfig)
    return key ?? ""
  }, [queryConfig])

  // Write to ID list and use batch updater for individual docs
  const setFilteredIds = useSetAtom(filteredDocIdsAtom(queryKey))
  const updateDocs = useSetAtom(updateDocsAtom)
  const setLoadingState = useSetAtom(atoms.setLoadingStateAtom)

  // Read from derived atom (automatically maps IDs → docs)
  const rawFilteredDocs = useAtomValue(filteredDocsAtom(queryKey))

  // Memoize to prevent new array reference when docs haven't actually changed.
  // We compare by element reference identity — Jotai returns new object refs for updated atoms.
  const prevFilteredDocsRef = useRef<T[]>([])
  const filteredDocs = useMemo(() => {
    const prev = prevFilteredDocsRef.current
    if (
      prev.length > 0 &&
      rawFilteredDocs.length === prev.length &&
      rawFilteredDocs.every((doc, i) => doc === prev[i])
    ) {
      return prev
    }

    prevFilteredDocsRef.current = rawFilteredDocs
    return rawFilteredDocs
  }, [rawFilteredDocs])

  useEffect(() => {
    const subscriptionStart = performance.now()
    perfMetrics.subscriptionCount++

    if (!isManagerLoggedIn || !queryKey) {
      // console.log(
      //   `${logPrefix} ⏸️ useFilteredDocs skipped (logged in: ${isManagerLoggedIn}, has key: ${!!queryKey})`
      // )
      return
    }

    // Infinite loop protection
    const loopKey = `useFilteredDocs:${docType}:${queryKey.slice(0, 100)}`
    if (checkInfiniteLoop(loopKey, logPrefix)) {
      perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
      scheduleLog()
      return // Subscription blocked
    }

    // console.log(
    //   `${logPrefix} 🔌 useFilteredDocs subscribing with query:`,
    //   queryConfig
    // )

    const firebaseStart = performance.now()
    let firstSnapshot = true
    const unsubscribe =
      Firebase.getInstance().createListenerForVariableQuery<T>(
        docType,
        queryConfig,
        (changes: Array<{ doc: T; type: string }>) => {
          // console.log(
          //   `${logPrefix} 📥 useFilteredDocs received ${changes.length} changes`
          // )

          // An empty first snapshot still means the query has answered.
          if (firstSnapshot) {
            firstSnapshot = false
            setLoadingState({
              type: "filtered",
              key: queryKey,
              state: { isInitialLoad: false, hasData: changes.length > 0 },
            })
          }

          // Update individual doc atoms atomically
          // IMPORTANT: For queries, "removed" means "no longer matches query", not "deleted"
          // So we only update documents that are "added" or "modified"
          // If a document was actually deleted, other listeners (useAllDocs, useOneDoc) will handle it
          const docUpdates = changes
            .filter(({ type }) => type !== "removed")
            .map(({ doc }) => ({
              id: doc.id,
              doc,
            }))
          if (docUpdates.length > 0) {
            updateDocs(docUpdates)
          }

          // Update ID list
          setFilteredIds((prevIds) => {
            const newIds = new Set(prevIds)

            changes.forEach(({ doc, type }: { doc: T; type: string }) => {
              if (type === "added" || type === "modified") {
                newIds.add(doc.id)
              } else if (type === "removed") {
                newIds.delete(doc.id)
              }
            })

            const finalIds = Array.from(newIds)
            // console.log(
            //   `${logPrefix} ✅ useFilteredDocs updated, total IDs: ${finalIds.length}`
            // )
            return finalIds
          })
        }
      )
    perfMetrics.totalPoolTime += performance.now() - firebaseStart

    perfMetrics.totalSubscriptionTime += performance.now() - subscriptionStart
    scheduleLog()

    return () => {
      // console.log(`${logPrefix} 🔌 useFilteredDocs unsubscribing`)
      unsubscribe()
    }
  }, [
    isManagerLoggedIn,
    docType,
    queryConfig,
    queryKey,
    logPrefix,
    setFilteredIds,
    setLoadingState,
    updateDocs,
  ])

  return filteredDocs
}
